import mime from 'mime'
import { createSelector } from 'reselect'

import type { GlobalState } from '../types'
import type { FileType } from './types'
import { EXCEEDED_MAX_CUMULATIVE,MAX_UPLOAD_SIZE } from './upload-constants'

const selectFiles = (state: GlobalState) => state.upload.files
const selectUploadErrors = (state: GlobalState) => state.uploadErrors
const selectIdentity = (state: GlobalState) => state.form.identityForm
const selectClaim = (state: GlobalState) => state.form.claimForm
const selectRequesting = (state: GlobalState) => state.upload.requesting
const selectFinished = (state: GlobalState) => state.upload.finished
const selectCategory = (state: GlobalState) => state.upload.category
const selectClaimHandlers = (state: GlobalState) => state.claim.claimDetails.professionals
const selectedClaimHandler = (state: GlobalState) => state.upload.handler
export const getTrackingError = (state: GlobalState) => state.upload.trackingError

const getExtensionMaybe = (str = '') => (str.match(/\.([A-z0-9]+)$/) || [])[1]

// This is a list of extensions that get treated as vidoes, ie they are s3 approved.
// This can be extended in the future for asks about proprietary video extensions that are not
// standardized extensions. For the backend, any extensions added here will also need to be added
// in the config of commonfilecabinet.
const lessSupportedVideoExtensions = [
  'flv',
  'ave',
  'dav',
  'edr',
  'g64',
  'g64x',
  'img',
  'rem',
  'mjp',
  'mjpg',
  'mjpeg',
  'var',
  'dce'
]

export const isS3Approved = (file: File): boolean => {
  const videoFileType = (file.type || '').startsWith('video/')
  const fileExtension = (getExtensionMaybe(file.name) || '').toLowerCase()
  const videoMimeType = (getMimeType(fileExtension)|| '').startsWith('video/')

  return videoFileType
    || lessSupportedVideoExtensions.includes(fileExtension)
    || videoMimeType
}

const getMimeType = (pathOrExtension: string) => mime.getType(pathOrExtension)

const sizeErrors = (size: number, errors: GlobalState['uploadErrors']) =>
  size - MAX_UPLOAD_SIZE > 0
    ? ([
        ...errors,
        {
          name: EXCEEDED_MAX_CUMULATIVE,
          error: `${((size - MAX_UPLOAD_SIZE) / 1000 / 1000).toFixed(3)}`
        }
      ])
    : errors

const parseErrors = (errors: GlobalState['uploadErrors']) =>
  errors.reduce<Record<string, string>>((acc, cur) => {
    if (!acc[cur.name]) acc[cur.name] = ''
    if (acc[cur.name] !== '') acc[cur.name] = `${acc[cur.name]}, ${cur.error}`
    else acc[cur.name] = `${acc[cur.name]} ${cur.error}`
    return acc
  }, {})


export const getFileSize = createSelector(
  selectFiles,
  files => files.reduce((a, f) => {
    if (isS3Approved(f.file)) {
      return a
    }
    return a + f.file.size
  }, 0)
)

export const getFileCount = createSelector(
  selectFiles,
  files => files.length
)

export const getIdentityValues = createSelector(
  selectIdentity,
  identity => ({
    name: identity.values?.identityName ?? '',
    email: identity.values?.identityEmail ?? '',
    role: identity.values?.roleInClaim ?? '',
  })
)

export const getClaimFormValues = createSelector(
  selectClaim,
  claim => ({
    claimNumber: claim.values?.claimNumber ?? '',
    dateOfLoss: claim.values?.dateOfLoss ?? '',
    roleInClaim: claim.values?.roleInClaim ?? ''
  })
)

export const getFilesLength = createSelector(
  selectFiles,
  files => files.length
)

export const getFileErrors = createSelector(
  selectFiles,
  files => files.reduce<{ fileError: string[], networkError: string[]}>((acc, cur) => {
    return (cur.error && cur.error.message === '400') ?
      {
        ...acc,
        fileError: [
          ...acc.fileError,
          cur.file.name
        ]
      }
      : (cur.error && cur.error.message !== '200')
        ? {
          ...acc,
          networkError: [
            ...acc.networkError,
            cur.file.name
          ]
        }
        : acc
  }, { fileError: [], networkError: [] })
)

export const getClaimHandlers = createSelector(
  selectClaimHandlers,
  claimHandlers => claimHandlers || []
)

export const getDisableSubmitStatus = createSelector(
  [getFileSize, selectRequesting, selectFinished],
  (size, requesting, finished) => size > MAX_UPLOAD_SIZE || requesting || finished
)

export const getUploadButtonDisabledStatus = createSelector(
  [selectCategory, selectFinished, getClaimHandlers, selectedClaimHandler],
  (category, finished, claimHandlers, selectedHander) =>
    category.length === 0
    || finished
    || (claimHandlers.length > 0 && !selectedHander)
)

export const getSortedFiles = createSelector(
  selectFiles,
  files => files.reduce<Record<string, FileType[]>>((acc, cur) => {
    if (!acc[cur.category]) acc[cur.category] = []
    acc[cur.category].push(cur)
    return acc
  }, {})
)

export const getErrors = createSelector(
  [getFileSize, selectUploadErrors],
  (fileSize: number, errors: GlobalState['uploadErrors']) =>
    parseErrors(sizeErrors(fileSize, errors))
)
